import { get, post, deleteAPI } from './Api'

// Webhook API service functions.
//
// The portal signs in as a BusinessAdmin, which is the same auth the backend
// webhook routes sit behind, so these go through the standard helpers.

export interface WebhookEndpoint {
  _id: string
  url: string
  active: boolean
  disabledAt: string | null
  disabledReason: string | null
  consecutiveFailures: number
  lastSuccessAt: string | null
  lastFailureAt: string | null
  secretLastRotatedAt: string | null
  createdAt: string
  updatedAt: string
  // Present ONLY in the create and rotate responses — never on a read.
  secret?: string
}

export interface WebhookDelivery {
  _id: string
  eventId: string
  type: string
  bookingId: string
  trackingId: string | null
  reference: string | null
  status: { code: string; description: string }
  previousStatus: { code: string | null; description: string | null }
  occurredAt: string
  state: 'pending' | 'delivering' | 'delivered' | 'failed' | 'discarded'
  attempts: number
  lastAttemptAt: string | null
  deliveredAt: string | null
  lastResponseStatus: number | null
  lastError: string | null
  createdAt: string
}

/** Read the current endpoint. `data` is null when none is configured. */
export const getWebhookEndpoint = async () => {
  try {
    return await get('business/webhooks')
  } catch (error) {
    throw error
  }
}

/**
 * Create the endpoint, or update the URL of an existing one.
 * On create the response carries the signing secret — shown once, never again.
 */
export const saveWebhookEndpoint = async (url: string, active?: boolean) => {
  try {
    const body: any = { url }
    if (active !== undefined) body.active = active
    return await post('business/webhooks', body, null)
  } catch (error) {
    throw error
  }
}

/** Issue a new signing secret. The old one stops working immediately. */
export const rotateWebhookSecret = async () => {
  try {
    return await post('business/webhooks/rotate-secret', {}, null)
  } catch (error) {
    throw error
  }
}

export const deleteWebhookEndpoint = async () => {
  try {
    return await deleteAPI('business/webhooks')
  } catch (error) {
    throw error
  }
}

/** Send a synthetic event and get the endpoint's actual HTTP response back. */
export const sendTestWebhook = async () => {
  try {
    return await post('business/webhooks/test', {}, null)
  } catch (error) {
    throw error
  }
}

export const getWebhookDeliveries = async (
  page: number = 1,
  limit: number = 10,
  state?: string,
) => {
  try {
    const query = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    })
    if (state) query.append('state', state)
    return await get(`business/webhooks/deliveries?${query.toString()}`)
  } catch (error) {
    throw error
  }
}

export const replayWebhookDelivery = async (eventId: string) => {
  try {
    return await post(
      `business/webhooks/deliveries/${eventId}/replay`,
      {},
      null,
    )
  } catch (error) {
    throw error
  }
}
